import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';

const CAT = {
  Operations: { color:'#2563eb', bg:'#eff6ff' },
  Storage:    { color:'#16a34a', bg:'#f0fdf4' },
  Egress:     { color:'#d97706', bg:'#fffbeb' },
  Other:      { color:'#64748b', bg:'#f8fafc' },
};
const skuCategory = (desc='') => {
  const d = desc.toLowerCase();
  if (d.includes('class a') || d.includes('class b') || d.includes('operation')) return 'Operations';
  if (d.includes('storage') && !d.includes('download')) return 'Storage';
  if (d.includes('download') || d.includes('egress') || d.includes('transfer')) return 'Egress';
  return 'Other';
};

const fmt  = n  => '₹' + Number(n||0).toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 });
const fmtK = n  => n >= 1000 ? `₹${(n/1000).toFixed(1)}K` : `₹${(n||0).toFixed(0)}`;
const pctOf = (n, total) => total > 0 ? ((n/total)*100).toFixed(1)+'%' : '0%';

const triggerDownload = async (params, format) => {
  const base  = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const qs    = new URLSearchParams({ ...params, format }).toString();
  const resp  = await fetch(`${base}/api/costs/download?${qs}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) throw new Error((await resp.json()).error || 'Download failed');
  const blob = await resp.blob();
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a'); a.href = url;
  a.download = `storage-costs.${format}`;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

export default function StorageCostPage() {
  const now  = new Date();
  const y    = now.getFullYear(), m = String(now.getMonth()+1).padStart(2,'0');
  const [startDate, setStartDate] = useState(`${y}-${m}-01`);
  const [endDate,   setEndDate]   = useState(`${y}-${m}-${String(new Date(y,now.getMonth()+1,0).getDate()).padStart(2,'0')}`);

  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [sortBy,    setSortBy]    = useState('cost');

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true); setError('');
    try {
      const { data: res } = await api.get('/costs/skus', {
        params: { service: 'Cloud Storage', startDate, endDate },
      });
      setData(res);
    } catch (err) {
      const e = err.response?.data;
      setError(e?.detail || e?.error || err.message);
      if (e?.action) setError(prev => prev + '\n\n→ ' + e.action);
    } finally { setLoading(false); }
  }, [startDate, endDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const skusWithCat = (data?.skus || []).map(r => ({ ...r, category: skuCategory(r.sku_description) }));

  const catTotals = skusWithCat.reduce((acc, r) => {
    acc[r.category] = (acc[r.category]||0) + (r.net_cost || r.total_cost); return acc;
  }, {});

  const pieData = Object.entries(catTotals)
    .filter(([,v]) => v > 0)
    .map(([name, value]) => ({ name, value: +value.toFixed(2), color: CAT[name]?.color || '#94a3b8' }))
    .sort((a,b) => b.value - a.value);

  const dailyMap = {};
  (data?.daily || []).forEach(d => {
    const date = typeof d.usage_date === 'string' ? d.usage_date : d.usage_date?.value;
    dailyMap[date] = (dailyMap[date]||0) + d.daily_cost;
  });
  const dailyData = Object.entries(dailyMap)
    .map(([day, cost]) => ({ day: day.slice(5), cost: +cost.toFixed(2) }))
    .sort((a,b) => a.day.localeCompare(b.day));

  const filtered = skusWithCat
    .filter(r => filterCat === 'all' || r.category === filterCat)
    .filter(r => !search || r.sku_description.toLowerCase().includes(search.toLowerCase()) || (r.sku_id||'').includes(search))
    .sort((a,b) => sortBy === 'cost' ? (b.net_cost||b.total_cost) - (a.net_cost||a.total_cost)
                 : sortBy === 'usage' ? b.total_usage - a.total_usage
                 : a.sku_description.localeCompare(b.sku_description));

  const total = data?.totalNetCost || data?.totalCost || 0;

  const quickRange = (days) => {
    const e = new Date(); e.setDate(e.getDate()-1);
    const s = new Date(e); s.setDate(s.getDate()-days+1);
    setStartDate(s.toISOString().slice(0,10));
    setEndDate(e.toISOString().slice(0,10));
  };

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>

        <div style={s.headerRow}>
          <div>
            <div style={s.breadcrumb}>Resources · SKU Cost Breakdown</div>
            <h1 style={s.title}>🪣 Cloud Storage</h1>
            <p style={s.subtitle}>Real usage & cost per SKU · from BigQuery Billing Export</p>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button style={s.outBtn} onClick={async()=>{
              const base=process.env.REACT_APP_API_URL||'http://localhost:5000';
              const token=localStorage.getItem('token')||sessionStorage.getItem('token');
              await fetch(`${base}/api/costs/cache`,{method:'DELETE',headers:{Authorization:`Bearer ${token}`}});
              setData(null); fetchData();
            }}>🗑 Clear Cache</button>
            <button style={s.outBtn} onClick={()=>triggerDownload({service:'Cloud Storage',startDate,endDate},'csv').catch(alert)} disabled={!data}>⬇ CSV</button>
            <button style={s.outBtn} onClick={()=>triggerDownload({service:'Cloud Storage',startDate,endDate},'json').catch(alert)} disabled={!data}>⬇ JSON</button>
          </div>
        </div>

        {/* ── Date Filter ── */}
        <div style={s.filterBar}>
          <div style={s.filterGroup}>
            <label style={s.label}>From</label>
            <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={s.dateInput}/>
          </div>
          <div style={s.filterGroup}>
            <label style={s.label}>To</label>
            <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={s.dateInput}/>
          </div>
          <div style={{display:'flex',gap:6,alignItems:'flex-end'}}>
            {[{l:'Last 7d',d:7},{l:'Last 14d',d:14},{l:'Last 30d',d:30}].map(q=>(
              <button key={q.d} style={s.quickBtn} onClick={()=>quickRange(q.d)}>{q.l}</button>
            ))}
            <button style={{...s.applyBtn,background:'linear-gradient(135deg,#14532d,#16a34a)',boxShadow:'0 2px 8px rgba(22,163,74,0.3)'}} onClick={fetchData} disabled={loading}>
              {loading ? '⏳ Loading…' : '🔍 Apply'}
            </button>
          </div>
          {data?.fromCache && <span style={s.cachePill}>⚡ Cached</span>}
          {data?.fetchedAt && !data.fromCache && <span style={s.livePill}>🟢 Live · {new Date(data.fetchedAt).toLocaleTimeString()}</span>}
        </div>

        {error && (
          <div style={s.errorBox}>
            <div style={{fontWeight:600,marginBottom:6}}>⚠ {error.split('\n\n→')[0]}</div>
            {error.includes('→') && <div style={{fontSize:12,color:'#92400e',marginTop:6}}>➡ {error.split('\n\n→')[1]}</div>}
          </div>
        )}

        {loading && (
          <div style={s.loadingWrap}>
            <div style={{...s.spinner,borderColor:'#bbf7d0',borderTopColor:'#16a34a'}}/>
            <span style={{color:'#64748b'}}>Querying BigQuery Billing Export…</span>
          </div>
        )}

        {data && !loading && (
          <>
            <div style={s.heroGrid}>
              <div style={{...s.heroCard,background:'linear-gradient(135deg,#14532d,#16a34a)',boxShadow:'0 4px 20px rgba(22,163,74,0.3)'}}>
                <div style={s.heroLabel}>Total Cost</div>
                <div style={s.heroVal}>{fmt(total)}</div>
                <div style={s.heroSub}>{startDate} → {endDate}</div>
                <div style={s.heroBadge}>{data.skuCount} SKUs · BigQuery Live</div>
              </div>
              {pieData.slice(0,4).map(cat => (
                <div key={cat.name} style={{...s.catCard, borderTop:`3px solid ${cat.color}`}}>
                  <div style={{...s.catLabel, color:cat.color}}>{cat.name}</div>
                  <div style={s.catVal}>{fmt(cat.value)}</div>
                  <div style={s.catSub}>{pctOf(cat.value,total)} of total</div>
                  <div style={s.miniBar}>
                    <div style={{height:'100%',width:pctOf(cat.value,total),background:cat.color,borderRadius:3}}/>
                  </div>
                </div>
              ))}
            </div>

            <div style={s.chartsRow}>
              <div style={{...s.card,flex:1.6}}>
                <div style={s.cardHeader}>
                  <h3 style={s.cardTitle}>Daily Cost Trend</h3>
                  <span style={s.badge}>{startDate} → {endDate}</span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={dailyData} margin={{left:0,right:10,top:5,bottom:0}}>
                    <defs>
                      <linearGradient id="gcsGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#16a34a" stopOpacity={0.15}/>
                        <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9"/>
                    <XAxis dataKey="day" tick={{fill:'#94a3b8',fontSize:10}} stroke="#e2e8f0"/>
                    <YAxis tick={{fill:'#64748b',fontSize:10}} stroke="#e2e8f0" tickFormatter={fmtK} width={65}/>
                    <Tooltip contentStyle={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.08)'}} formatter={v=>[fmt(v),'Cost']}/>
                    <Area type="monotone" dataKey="cost" stroke="#16a34a" strokeWidth={2} fill="url(#gcsGrad)" dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div style={{...s.card,flex:1}}>
                <div style={s.cardHeader}>
                  <h3 style={s.cardTitle}>Cost Share</h3>
                  <span style={s.badge}>by category</span>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} stroke="#fff" strokeWidth={2}>
                      {pieData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                    </Pie>
                    <Tooltip contentStyle={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:8}} formatter={v=>[fmt(v),'Cost']}/>
                    <Legend formatter={v=><span style={{color:'#64748b',fontSize:11}}>{v}</span>}/>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* ── SKU Table ── */}
            <div style={{...s.card,padding:0,overflow:'hidden',marginBottom:12}}>
              <div style={{...s.cardHeader,padding:'14px 20px',borderBottom:'1px solid #f1f5f9',flexWrap:'wrap',gap:10}}>
                <h3 style={s.cardTitle}>SKU-Level Breakdown ({filtered.length} of {data.skuCount} SKUs)</h3>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search SKU…" style={s.input}/>
                  <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={s.select}>
                    <option value="all">All Categories</option>
                    {Object.keys(CAT).map(c=><option key={c}>{c}</option>)}
                  </select>
                  <select value={sortBy} onChange={e=>setSortBy(e.target.value)} style={s.select}>
                    <option value="cost">Sort: Cost ↓</option>
                    <option value="usage">Sort: Usage ↓</option>
                    <option value="name">Sort: Name</option>
                  </select>
                </div>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={s.table}>
                  <thead>
                    <tr style={{background:'#f8fafc'}}>
                      {['SKU Description','Category','SKU ID','Region','Usage','Unit','Days Active','Cost (₹)','Share'].map(h=>(
                        <th key={h} style={s.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r,i) => {
                      const cat = CAT[r.category] || CAT.Other;
                      return (
                        <tr key={i}
                          onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                          onMouseLeave={e=>e.currentTarget.style.background='#fff'}
                          style={{background:'#fff'}}
                        >
                          <td style={{...s.td,maxWidth:260}}>
                            <div style={{fontWeight:500,color:'#0f172a',lineHeight:1.4}}>{r.sku_description}</div>
                          </td>
                          <td style={s.td}>
                            <span style={{background:cat.bg,color:cat.color,padding:'2px 8px',borderRadius:20,fontSize:11,fontWeight:700,whiteSpace:'nowrap'}}>
                              {r.category}
                            </span>
                          </td>
                          <td style={s.td}><code style={s.code}>{r.sku_id}</code></td>
                          <td style={s.td}><span style={{color:'#64748b',fontSize:12}}>{r.region||'—'}</span></td>
                          <td style={{...s.td,fontFamily:'monospace',fontSize:12}}>
                            {Number(r.total_usage).toLocaleString('en-IN',{maximumFractionDigits:3})}
                          </td>
                          <td style={s.td}><span style={{color:'#94a3b8',fontSize:11}}>{r.unit}</span></td>
                          <td style={{...s.td,textAlign:'center'}}>
                            <span style={{background:'#f1f5f9',color:'#475569',padding:'1px 8px',borderRadius:20,fontSize:11}}>{r.days_active}d</span>
                          </td>
                          <td style={{...s.td,whiteSpace:'nowrap'}}>
                            <div style={{fontWeight:700,color:'#0f172a'}}>{fmt(r.net_cost||r.total_cost)}</div>
                            {r.total_credits < 0 && <div style={{fontSize:11,color:'#16a34a'}}>Credits: {fmt(r.total_credits)}</div>}
                          </td>
                          <td style={s.td}>
                            <div style={{display:'flex',alignItems:'center',gap:7}}>
                              <div style={{width:52,height:5,background:'#f1f5f9',borderRadius:3,overflow:'hidden'}}>
                                <div style={{width:pctOf(r.net_cost||r.total_cost,total),height:'100%',background:cat.color,borderRadius:3}}/>
                              </div>
                              <span style={{color:'#64748b',fontSize:11,minWidth:38}}>{pctOf(r.net_cost||r.total_cost,total)}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    <tr style={{background:'#f0fdf4'}}>
                      <td style={{...s.td,fontWeight:700,color:'#166534',fontSize:14}} colSpan={7}>TOTAL</td>
                      <td style={{...s.td,fontWeight:800,color:'#166534',fontSize:15,whiteSpace:'nowrap'}}>{fmt(total)}</td>
                      <td style={{...s.td,fontWeight:700,color:'#166534'}}>100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={s.srcNote}>
              Source: BigQuery Billing Export · Period: {startDate} to {endDate}
              {data.fromCache && ' · ⚡ Cached result'}
            </div>
          </>
        )}

        {!loading && !data && !error && (
          <div style={s.emptyState}>
            <div style={{fontSize:40,marginBottom:12}}>🪣</div>
            <div style={{color:'#0f172a',fontWeight:600,fontSize:16}}>Select a date range and click Apply</div>
            <div style={{color:'#94a3b8',fontSize:13,marginTop:4}}>Requires BQ_BILLING_TABLE set in backend .env</div>
          </div>
        )}
      </main>
    </div>
  );
}

const s = {
  layout:{display:'flex',background:'#f5f7fa',minHeight:'100vh',fontFamily:"'Inter','Segoe UI',sans-serif"},
  main: { marginLeft:0, flex:1, padding:'12px 12px' },
  headerRow:{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:16},
  breadcrumb:{color:'#94a3b8',fontSize:12,marginBottom:4},
  title:{margin:0,color:'#0f172a',fontSize:24,fontWeight:700,letterSpacing:'-0.3px'},
  subtitle:{margin:'4px 0 0',color:'#94a3b8',fontSize:13},
  outBtn:{background:'#fff',border:'1.5px solid #e2e8f0',color:'#374151',borderRadius:8,padding:'8px 16px',cursor:'pointer',fontSize:13,fontWeight:500},
  filterBar:{display:'flex',gap:16,alignItems:'flex-end',background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'16px 20px',marginBottom:20,flexWrap:'wrap'},
  filterGroup:{display:'flex',flexDirection:'column',gap:4},
  label:{color:'#64748b',fontSize:11,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.5px'},
  dateInput:{border:'1.5px solid #e2e8f0',borderRadius:8,padding:'7px 12px',fontSize:13,color:'#0f172a',background:'#f8fafc',outline:'none'},
  quickBtn:{background:'#f1f5f9',border:'1px solid #e2e8f0',color:'#475569',borderRadius:8,padding:'7px 12px',cursor:'pointer',fontSize:12},
  applyBtn:{border:'none',color:'#fff',borderRadius:8,padding:'8px 20px',cursor:'pointer',fontSize:13,fontWeight:600},
  cachePill:{background:'#fef9c3',color:'#854d0e',padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600},
  livePill:{background:'#dcfce7',color:'#166534',padding:'3px 10px',borderRadius:20,fontSize:11,fontWeight:600},
  errorBox:{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'14px 18px',marginBottom:20,color:'#dc2626',whiteSpace:'pre-line'},
  loadingWrap:{display:'flex',alignItems:'center',gap:14,padding:60,justifyContent:'center'},
  spinner:{width:28,height:28,border:'3px solid #e2e8f0',borderTopColor:'#94a3b8',borderRadius:'50%',animation:'spin 0.8s linear infinite'},
  heroGrid:{display:'grid',gridTemplateColumns:'1.4fr 1fr 1fr 1fr',gap:14,marginBottom:18},
  heroCard:{borderRadius:14,padding:'22px'},
  heroLabel:{color:'rgba(255,255,255,0.65)',fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.8px',marginBottom:8},
  heroVal:{color:'#fff',fontSize:26,fontWeight:800,letterSpacing:'-0.5px',marginBottom:4},
  heroSub:{color:'rgba(255,255,255,0.5)',fontSize:11,marginBottom:10},
  heroBadge:{color:'rgba(255,255,255,0.85)',fontSize:11,padding:'3px 10px',borderRadius:20,background:'rgba(255,255,255,0.15)',display:'inline-block'},
  catCard:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:14,padding:'16px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  catLabel:{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:6},
  catVal:{color:'#0f172a',fontSize:17,fontWeight:700,marginBottom:3},
  catSub:{color:'#94a3b8',fontSize:11,marginBottom:8},
  miniBar:{height:4,background:'#f1f5f9',borderRadius:3,overflow:'hidden'},
  chartsRow:{display:'flex',gap:16,marginBottom:16},
  card:{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'20px',boxShadow:'0 1px 4px rgba(0,0,0,0.04)'},
  cardHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14},
  cardTitle:{margin:0,color:'#0f172a',fontSize:15,fontWeight:600},
  badge:{background:'#f1f5f9',color:'#64748b',fontSize:11,padding:'3px 10px',borderRadius:20,whiteSpace:'nowrap'},
  table:{width:'100%',borderCollapse:'collapse'},
  th:{textAlign:'left',color:'#64748b',fontSize:10,fontWeight:600,letterSpacing:'0.5px',textTransform:'uppercase',padding:'10px 14px',borderBottom:'1px solid #f1f5f9',whiteSpace:'nowrap'},
  td:{padding:'10px 14px',color:'#374151',fontSize:13,borderBottom:'1px solid #f8fafc'},
  input:{background:'#f8fafc',border:'1.5px solid #e2e8f0',borderRadius:8,padding:'6px 12px',fontSize:13,color:'#0f172a',outline:'none',width:160},
  select:{background:'#f8fafc',border:'1.5px solid #e2e8f0',borderRadius:8,padding:'6px 10px',fontSize:12,color:'#374151',outline:'none'},
  code:{background:'#f1f5f9',padding:'2px 6px',borderRadius:4,fontFamily:'monospace',fontSize:11,color:'#475569'},
  srcNote:{color:'#94a3b8',fontSize:11,textAlign:'center',paddingTop:4,marginBottom:8},
  emptyState:{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:80,color:'#94a3b8'},
};